import { useEffect, useState } from "react";
import { Hourglass, Trophy, Clock, Star, Sparkles, Flame, Bandage, Eye, EyeOff, Gavel, TrendingUp, Shield, PawPrint } from "lucide-react";
import { CLASS_META, LIFECYCLE, CLASS_IMAGE } from "@/lib/game";
import { StatBars } from "@/components/StatBars";

export function LifecycleBadge({ lifecycle }) {
  const lc = LIFECYCLE[lifecycle] || LIFECYCLE.prime;
  return (
    <span data-testid={`lifecycle-${lifecycle}`} className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm ${lc.cls}`}>
      {lc.label}
    </span>
  );
}

export function DurabilityBar({ value, compact = false }) {
  if (value == null) return null;
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const color = v > 50 ? "bg-emerald-500" : v > 20 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className={compact ? "mt-1" : "mt-2"} title={`Durability ${v}%`} data-testid="durability-bar">
      <div className="flex justify-between text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">
        <span>Durability</span><span className={v > 20 ? "text-zinc-400" : "text-red-400"}>{v}%</span>
      </div>
      <div className="h-1 bg-zinc-800 rounded-sm overflow-hidden"><div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }} /></div>
    </div>
  );
}

function fmtCooldown(s) {
  if (s <= 0) return "ready";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export function CooldownChip({ seconds, testId }) {
  const [left, setLeft] = useState(seconds || 0);
  useEffect(() => { setLeft(seconds || 0); }, [seconds]);
  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setLeft((p) => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [left]);
  if (left <= 0) return null;
  return (
    <span data-testid={testId} title="This gladiator has used their fight and is resting"
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-orange-600/50 bg-orange-950/30 text-orange-300 rounded-sm tabular-nums">
      <Clock size={11} /> Resting · {fmtCooldown(left)}
    </span>
  );
}

export function ScoutStars({ band, testId }) {  const b = band || {};
  const progress = b.exact ? 1 : (b.progress || 0);
  const filled = b.exact ? 3 : progress >= 0.66 ? 3 : progress >= 0.33 ? 2 : b.revealed ? 1 : 0;
  const pct = Math.round(progress * 100);
  const title = b.revealed
    ? (b.exact ? "Scouting complete — true potential known" : `Scout confidence: ${pct}% — keep training to sharpen the read`)
    : "Unscouted — train this fighter to read their potential";
  return (
    <span data-testid={testId} title={title} className="inline-flex items-center gap-0.5 align-middle">
      {[0, 1, 2].map((i) => (
        <Star key={i} size={11}
          className={i < filled ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />
      ))}
    </span>
  );
}

export const POTENTIAL_STARS = { Poor: 1, Average: 2, Good: 3, Excellent: 4, Elite: 5 };

export function PotentialStars({ band, testId }) {
  const b = band || {};
  // Only reveal grade stars once scouting is complete (exact grade known).
  if (!b.exact) return null;
  const count = POTENTIAL_STARS[b.label] || 0;
  if (!count) return null;
  return (
    <span data-testid={testId} title={`Potential: ${b.label} (${count}/5)`} className="inline-flex items-center gap-0.5 align-middle">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={12} className={i < count ? "text-amber-400 fill-amber-400" : "text-zinc-700"} />
      ))}
    </span>
  );
}

export function StreakBadge({ streak, testId }) {
  if (!streak || streak < 2) return null;
  return (
    <span data-testid={testId} title={`${streak}-win streak`}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-orange-600/50 bg-orange-950/30 text-orange-300 rounded-sm tabular-nums">
      <Flame size={11} className="fill-orange-500/40" /> {streak} streak
    </span>
  );
}

export function ScarBadge({ scars, testId }) {
  if (!scars || scars.length === 0) return null;
  const title = "Battle scars — " + scars.map((s) => `${s.name} (−${s.pct}% ${s.neg}, +${s.pct}% ${s.pos}, −2% training speed)`).join("; ");
  return (
    <span data-testid={testId} title={title}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-red-800/50 bg-red-950/30 text-red-300 rounded-sm tabular-nums">
      <Bandage size={11} /> {scars.length}
    </span>
  );
}

export function SpecializationBadge({ specialization, testId }) {
  if (!specialization) return null;
  const bonusPct = Math.round(specialization.bonus_pct * 100);
  const title = `Specialized Training by ${specialization.doctore_name}\nBoosted: ${specialization.boosts.join(', ')} (+${bonusPct}%)\nPenalized: ${specialization.penalties.join(', ')} (−${bonusPct}%)`;
  return (
    <span data-testid={testId} title={title}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-amber-600/50 bg-amber-950/30 text-amber-300 rounded-sm">
      <Shield size={11} /> Specialized
    </span>
  );
}

export default function GladiatorCard({ g, onClick, footer, showStats = true, enableBaseStatsToggle = false }) {
  const meta = CLASS_META[g.cls] || {};
  const Icon = meta.icon;
  const classImageUrl = CLASS_IMAGE[g.cls] || CLASS_IMAGE.Provocator;
  const [showBaseStats, setShowBaseStats] = useState(false);

  useEffect(() => {
    setShowBaseStats(false);
  }, [g.id]);

  const effectiveSkills = g.combat_skills || g.effective_skills || g.peak_effective_skills || g.skills;
  const effectiveSkillTitles = g.skill_titles || g.peak_raw_skill_titles;
  const hasCurrentBaseSkills = Object.values(g.skills || {}).some((value) => Number(value || 0) > 0);
  const baseSkills = hasCurrentBaseSkills ? (g.skills || effectiveSkills) : (g.peak_raw_skills || effectiveSkills);
  const baseSkillTitles = hasCurrentBaseSkills
    ? (g.skill_titles || g.peak_raw_skill_titles)
    : (g.peak_raw_skill_titles || g.skill_titles);
  const statsHeading = showBaseStats ? "Base Stats" : "Current Combat Skills";
  const statsSubheading = showBaseStats ? "No gear or temporary bonuses" : "With gear";

  const toggleBaseStats = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowBaseStats((current) => !current);
  };
  
  return (
    <div data-testid={`gladiator-card-${g.id}`} onClick={onClick}
      className={`bg-zinc-950 border border-amber-700/30 hover:border-amber-600/60 shadow-[0_0_15px_rgba(0,0,0,0.5)] p-4 transition-all duration-300 ${onClick ? "cursor-pointer hover:-translate-y-1 hover:shadow-[0_4px_20px_rgba(180,83,9,0.15)]" : ""}`}>
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={meta.color} size={22} />}
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-manrope">{meta.tag}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {g.auctioned && (
            <span data-testid={`auctioned-badge-${g.id}`} title="Auctioned: Currently on the Block"
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-fuchsia-600/50 bg-fuchsia-950/30 text-fuchsia-300 rounded-sm">
              <Gavel size={11} /> On the Block
            </span>
          )}
          {g.equipped_beast && (() => {
            // Calculate time remaining on 24h lock
            let hoursRemaining = 0;
            
            try {
              if (g.equipped_beast.equipped_at) {
                const equippedAt = new Date(g.equipped_beast.equipped_at);
                const now = new Date();
                
                // Check if date is valid
                if (!isNaN(equippedAt.getTime())) {
                  const msElapsed = now - equippedAt;
                  const hoursElapsed = msElapsed / (1000 * 60 * 60);
                  hoursRemaining = Math.max(0, Math.floor(24 - hoursElapsed));
                }
              }
            } catch (e) {
              console.error('Error calculating beast lock time:', e, g.equipped_beast.equipped_at);
              // Default to 0 (expired) if calculation fails
              hoursRemaining = 0;
            }
            
            const lockExpired = hoursRemaining === 0;
            
            // Rarity-based colors
            const rarityStyles = {
              common: "border-zinc-600/50 bg-zinc-900/30 text-zinc-300",
              uncommon: "border-emerald-600/50 bg-emerald-950/30 text-emerald-300",
              rare: "border-blue-600/50 bg-blue-950/30 text-blue-300",
              epic: "border-purple-600/50 bg-purple-950/30 text-purple-300",
              legendary: "border-amber-600/50 bg-amber-950/30 text-amber-300"
            };
            const rarity = g.equipped_beast.rarity || "common";
            const style = rarityStyles[rarity] || rarityStyles.common;
            
            // Show equipped_at in tooltip for debugging if present
            const tooltipText = !g.equipped_beast.equipped_at
              ? `Beast: ${g.equipped_beast.name} (lock status unknown)`
              : lockExpired 
                ? `Beast: ${g.equipped_beast.name} (can unequip)`
                : `Beast: ${g.equipped_beast.name} (${hoursRemaining}h lock remaining)`;
            
            return (
              <span data-testid={`beast-badge-${g.id}`} title={tooltipText}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm ${style}`}>
                <PawPrint size={11} /> Beast
                {!lockExpired && hoursRemaining > 0 && <span className="text-[9px] opacity-70">{hoursRemaining}h</span>}
              </span>
            );
          })()}
          <ScarBadge scars={g.scars} testId={`scar-badge-${g.id}`} />
          <SpecializationBadge specialization={g.specialization} testId={`specialization-badge-${g.id}`} />
          <LifecycleBadge lifecycle={g.lifecycle} />
        </div>
      </div>
      
      {/* Class image above name */}
      <div className="flex items-start gap-3 mt-3">
        <img 
          src={classImageUrl} 
          alt={g.cls}
          className="w-14 h-14 object-cover object-top border border-zinc-700 flex-shrink-0"
          style={{ filter: 'brightness(90%)' }}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-cinzel text-lg text-amber-300 tracking-wide uppercase truncate">{g.name}</h3>
          <div className="flex items-center gap-4 mt-1 text-xs text-zinc-400">
            <span className="flex items-center gap-1"><Hourglass size={12} /> Age {g.age}</span>
            <span className="flex items-center gap-1"><Trophy size={12} /> {g.rating}</span>
            <span className="text-zinc-500">{g.wins}W / {g.losses}L</span>
          </div>
          {/* Ranking display */}
          {g.rank && (
            <div className="flex items-center gap-1 mt-1 text-xs text-amber-400">
              <TrendingUp size={12} />
              <span className="font-semibold">#{g.rank}</span>
              <span className="text-zinc-500">Current rank</span>
            </div>
          )}
          {g.elo_division_rank && (
            <div data-testid={`gladiator-division-rank-${g.id}`} className="mt-1 text-xs text-zinc-400">
              <span className="text-amber-300 font-semibold">{g.elo_division_rank.name}</span>
              <span className="text-zinc-600 px-1.5">·</span>
              <span>Division rank <span className="text-zinc-200 font-semibold">#{g.elo_division_rank.rank}</span></span>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-zinc-500">Peak <span className="text-amber-400 font-semibold">{g.skills_hidden ? "—" : (g.peak_overall ?? g.overall)}</span></span>
        <span className="text-zinc-500 flex items-center gap-1.5">Potential <span className="text-violet-400 font-semibold">{g.potential_grade}</span>
          {!g.retired && <PotentialStars band={g.potential_band} testId={`potential-stars-${g.id}`} />}
        </span>
      </div>
      {!g.retired && !g.fight_ready && (
        <div className="mt-3"><CooldownChip seconds={g.fight_cooldown_seconds} testId={`cooldown-chip-${g.id}`} /></div>
      )}
      {!g.retired && g.skill_points > 0 && (
        <div className="mt-3">
          <span data-testid={`skillpoint-chip-${g.id}`} title="Unspent skill points — open the Skill Tree"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-amber-600/50 bg-amber-950/30 text-amber-300 rounded-sm">
            <Sparkles size={11} /> {g.skill_points} skill {g.skill_points === 1 ? "point" : "points"}
          </span>
        </div>
      )}
      {showStats && (g.skills_hidden
        ? <div className="mt-4 text-[11px] text-zinc-600 italic flex items-center gap-1.5"><EyeOff size={12} /> Skills hidden by owner</div>
        : (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div data-testid={`stats-mode-label-${g.id}`} className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{statsHeading}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600">{statsSubheading}</div>
              </div>
              {enableBaseStatsToggle && (
                <button
                  type="button"
                  data-testid={`toggle-base-stats-${g.id}`}
                  onClick={toggleBaseStats}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border rounded-sm transition-colors ${
                    showBaseStats
                      ? "border-amber-600/70 bg-amber-950/30 text-amber-200"
                      : "border-zinc-700 text-zinc-400 hover:border-amber-600/70 hover:text-amber-200"
                  }`}
                  aria-pressed={showBaseStats}
                >
                  {showBaseStats ? <Eye size={11} /> : <EyeOff size={11} />}
                  {showBaseStats ? "Hide Base Stats" : "View Base Stats"}
                </button>
              )}
            </div>
            <div
              data-testid={showBaseStats ? `base-stats-panel-${g.id}` : `effective-stats-${g.id}`}
              className={`border p-3 transition-colors ${showBaseStats ? "border-amber-700/50 bg-amber-950/10" : "border-zinc-800 bg-zinc-950/60"}`}
            >
              <StatBars
                skills={showBaseStats ? baseSkills : effectiveSkills}
                skillTitles={showBaseStats ? baseSkillTitles : effectiveSkillTitles}
                compact
              />
            </div>
          </div>
        ))}
      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
}
